import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "../components/Loader";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_SERIES,
  OTHER_COLOR,
} from "../shared/chartTheme";

import {
  fetchWarehouseReport,
  selectWarehouseData,
  selectWarehouseError,
  selectWarehouseStatus,
} from "../features/reports/reportsSlice";
import { fetchStores, selectStores, selectStoresStatus } from "../features/stores/storesSlice";
import StoreMultiSelect from "../components/StoreMultiSelect";

import "./ReportPage.css";

/**
 * Страница «Склад» — замена дашборда Metabase `3-auf-sklad` (AUFB-65).
 *
 * Панели: общая стоимость товаров, доли товаров в остатке, остатки по магазинам
 * и по товарам, и четыре панели по браку. Данные — `/api/reports/warehouse/`.
 *
 * Период на этой странице действует только на брак: у остатка дат нет, таблица
 * `Inventory` хранит текущее количество, а не движения.
 */

const CHART_COLORS = CHART_SERIES;


function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Крупные суммы в KPI — без копеек, иначе 201 872 073.00 не читается. */
function formatMoneyShort(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("ru-RU", { maximumFractionDigits: 0 });
}

function formatCount(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("ru-RU");
}

function formatQuantity(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return Number.isInteger(num) ? num.toLocaleString("ru-RU") : num.toLocaleString("ru-RU", { maximumFractionDigits: 3 });
}

function formatAxisMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  return String(Math.round(n));
}

function toIsoLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

function KpiCard({ title, value, hint, accent }) {
  return (
    <div className="kpi-card">
      <div className="kpi-title">{title}</div>
      <div className={accent ? `kpi-value ${accent}` : "kpi-value"}>{value}</div>
      {hint && <div className="kpi-hint">{hint}</div>}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="section">
      <div className="section-head">
        <div className="section-title">{title}</div>
        {subtitle && <div className="section-subtitle">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Empty({ text = "Нет данных" }) {
  return <div className="empty">{text}</div>;
}

function BreakdownTable({ rows, columns, emptyText, footer }) {
  if (!rows || rows.length === 0) return <Empty text={emptyText} />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={col.numeric ? "numeric" : undefined}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id ?? index}>
              {columns.map((col) => {
                const raw = row[col.key];
                let content = raw;
                if (col.money) content = formatMoney(raw);
                else if (col.quantity) content = formatQuantity(raw);
                else if (col.numeric) content = formatCount(raw);
                else if (raw === null || raw === undefined || raw === "") content = "—";
                const cellClasses = [col.numeric ? "numeric" : "", col.bold ? "bold" : ""].filter(Boolean).join(" ");
                return (
                  <td key={col.key} className={cellClasses || undefined}>
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {footer && <div className="hint-block">{footer}</div>}
    </div>
  );
}

const tooltipStyle = {
  background: "var(--c-card)",
  border: "1px solid var(--c-border)",
  borderRadius: 8,
  color: "var(--c-text)",
  fontSize: 14,
};

/** Donut «топ + Другое»: доли считаются от общего количества, как в Metabase. */
function ShareDonut({ items, other, total, valueKey = "quantity" }) {
  const data = useMemo(() => {
    const rows = (items || []).map((item) => ({
      name: item.product_name,
      value: Number(item[valueKey] || 0),
    }));
    const otherValue = Number(other?.quantity || 0);
    if (otherValue > 0) rows.push({ name: "Другое", value: otherValue, isOther: true });
    return rows;
  }, [items, other, valueKey]);

  if (data.length === 0) return <Empty />;

  const totalNum = Number(total || 0);

  return (
    <ResponsiveContainer width="100%" height={320}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={80} outerRadius={120} paddingAngle={2}>
          {data.map((row, index) => (
            <Cell key={row.name} fill={row.isOther ? OTHER_COLOR : CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(value, name) => {
            const share = totalNum ? ((Number(value) / totalNum) * 100).toFixed(2) : "0.00";
            return [`${formatQuantity(value)} (${share}%)`, name];
          }}
        />
        <Legend wrapperStyle={{ fontSize: 14 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default function WarehousePage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);

  const data = useSelector(selectWarehouseData);
  const status = useSelector(selectWarehouseStatus);
  const error = useSelector(selectWarehouseError);

  const [storeIds, setStoreIds] = useState(() => {
    const raw = searchParams.getAll("store_ids");
    return raw.length ? raw.map(String) : [];
  });
  // Пустой период = брак за всё время, как на дашборде с незаполненным фильтром «Дата».
  const [startDate, setStartDate] = useState(() => searchParams.get("start_date") || "");
  const [endDate, setEndDate] = useState(() => searchParams.get("end_date") || "");

  useEffect(() => {
    if (storesStatus === "idle") dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch, storesStatus]);

  const load = useCallback(() => {
    const numericIds = storeIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    dispatch(
      fetchWarehouseReport({
        storeIds: numericIds,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        topProducts: 20,
        topDefective: 20,
      })
    );
  }, [dispatch, storeIds, startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams();
    storeIds.forEach((id) => params.append("store_ids", String(id)));
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    setSearchParams(params, { replace: true });
  }, [storeIds, startDate, endDate, setSearchParams]);

  const handleReset = () => {
    setStoreIds([]);
    setStartDate("");
    setEndDate("");
  };

  const totals = data?.totals;
  const byProduct = data?.by_product;
  const defective = data?.defective;

  const productRows = useMemo(
    () => (byProduct?.items || []).map((row, i) => ({ ...row, id: row.product_id ?? i })),
    [byProduct]
  );

  const storeRows = useMemo(
    () =>
      (data?.by_store || []).map((row) => ({
        ...row,
        id: row.store_id,
        quantity_num: Number(row.quantity || 0),
      })),
    [data]
  );

  const defectiveRows = useMemo(
    () => (defective?.items || []).map((row, i) => ({ ...row, id: row.product_id ?? i })),
    [defective]
  );

  const defectiveStoreRows = useMemo(
    () =>
      (defective?.by_store || []).map((row) => ({
        ...row,
        id: row.store_id,
        quantity_num: Number(row.quantity || 0),
      })),
    [defective]
  );

  return (
    <div className="sales-dashboard-page">
      <div className="header">
        <div>
          <div className="title">Склад</div>
          <div className="subtitle">Остатки товаров и брак по магазинам</div>
        </div>
      </div>

      <div className="toolbar">
        <StoreMultiSelect stores={stores} value={storeIds} onChange={setStoreIds} />
        <div className="field">
          <label className="label">Брак с даты</label>
          <DatePicker
            selected={parseIsoLocal(startDate)}
            onChange={(d) => setStartDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="за всё время"
            isClearable
            customInput={<input className="input" />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div className="field">
          <label className="label">Брак по дату</label>
          <DatePicker
            selected={parseIsoLocal(endDate)}
            onChange={(d) => setEndDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="за всё время"
            isClearable
            customInput={<input className="input" />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
          <div className="hint">Период действует только на брак</div>
        </div>
        <div className="toolbar-buttons">
          <button type="button" onClick={load} className="apply-btn">
            Обновить
          </button>
          <button type="button" onClick={handleReset} className="reset-btn">
            Сбросить
          </button>
        </div>
      </div>

      {status === "loading" && <Skeleton variant="cards" count={4} text="Загрузка отчёта…" style={{ padding: "16px 24px 32px" }} />}
      {status === "failed" && error && <div className="error-box">{error}</div>}

      <div className="body">
        <div className="kpi-grid">
          <KpiCard
            title="Общая стоимость товаров"
            value={formatMoneyShort(totals?.total_value)}
            accent="accent"
            hint="по розничным ценам"
          />
          <KpiCard title="Всего товаров" value={formatQuantity(totals?.total_quantity)} hint="штук на остатке" />
          <KpiCard title="Позиций" value={formatCount(totals?.products)} hint={`в ${formatCount(totals?.stores)} магазинах`} />
          <KpiCard
            title="Бракованных товаров"
            value={formatQuantity(defective?.totals?.total_quantity)}
            accent="danger"
            hint={`на ${formatMoneyShort(defective?.totals?.total_value)} сом`}
          />
        </div>

        <div className="two-col">
          <Section title="Доли товаров в остатке" subtitle={`Всего ${formatQuantity(byProduct?.total_quantity)} шт.`}>
            <ShareDonut items={byProduct?.items} other={byProduct?.other} total={byProduct?.total_quantity} />
          </Section>

          <Section title="Количество товаров в магазинах">
            {storeRows.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={storeRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                  <XAxis dataKey="store" stroke="var(--c-text-muted)" fontSize={10} interval={0} angle={-45} textAnchor="end" height={80} />
                  <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatQuantity(v)} />
                  <Bar dataKey="quantity_num" name="Количество" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Section>
        </div>

        <Section title="Количество товаров" subtitle="Топ по остатку">
          <BreakdownTable
            rows={productRows}
            columns={[
              { key: "product_name", title: "Название" },
              { key: "barcode", title: "Barcode" },
              { key: "price", title: "Стоимость (сом)", numeric: true, money: true },
              { key: "quantity", title: "Количество", numeric: true, quantity: true },
              { key: "total_value", title: "Общая стоимость", numeric: true, money: true, bold: true },
            ]}
            footer={`Показано ${productRows.length} из ${formatCount(byProduct?.total_positions)} позиций`}
            emptyText="На складе нет товаров"
          />
        </Section>

        <Section title="Количество товаров в магазинах (таблица)">
          <BreakdownTable
            rows={storeRows}
            columns={[
              { key: "store", title: "Магазин" },
              { key: "quantity", title: "Количество", numeric: true, quantity: true },
              { key: "positions", title: "Позиций", numeric: true },
              { key: "total_value", title: "Стоимость", numeric: true, money: true, bold: true },
            ]}
            footer={`${storeRows.length} строк`}
          />
        </Section>

        <div className="two-col">
          <Section title="Бракованные товары" subtitle="Топ по количеству">
            <BreakdownTable
              rows={defectiveRows}
              columns={[
                { key: "product_name", title: "Название" },
                { key: "barcode", title: "Barcode" },
                { key: "quantity", title: "Количество", numeric: true, quantity: true },
              ]}
              footer={`Показано ${defectiveRows.length} из ${formatCount(defective?.total_positions)} позиций`}
              emptyText="Брака за период нет"
            />
          </Section>

          <Section
            title="Общее количество бракованных товаров"
            subtitle={`Всего ${formatQuantity(defective?.totals?.total_quantity)} шт.`}
          >
            <ShareDonut
              items={defective?.items}
              other={defective?.other}
              total={defective?.totals?.total_quantity}
            />
          </Section>
        </div>

        <Section title="Бракованные товары в магазинах">
          {defectiveStoreRows.length === 0 ? (
            <Empty text="Брака за период нет" />
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={defectiveStoreRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                <XAxis dataKey="store" stroke="var(--c-text-muted)" fontSize={10} interval={0} angle={-45} textAnchor="end" height={80} />
                <YAxis stroke="var(--c-text-muted)" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatQuantity(v)} />
                <Bar dataKey="quantity_num" name="Брак" fill="var(--c-s5)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>
    </div>
  );
}
